/**
 * Admin V3 — Operations / Live — 5-card KPI strip
 *
 * Mirrors the 5-column KPI grid in legacy AdminControlTower.js:295.
 *
 * KPIs (in order):
 *   1. Bookings actives        — Activity icon, neutral slate
 *   2. Anomalies               — AlertTriangle icon, rose when > 0 (alert tone)
 *   3. Chauffeurs en ligne     — Car icon, neutral slate
 *   4. Versements en attente   — Euro icon, neutral slate (owner ruling #2)
 *   5. Incidents ouverts       — AlertCircle icon, rose when > 0 (alert tone)
 *
 * READ-ONLY: KPI cards are display surfaces only. Counts come from the same
 * /admin/control-tower/live payload as the dispatch table — no extra fetches.
 */

import React from 'react';
import { Activity, AlertTriangle, Car, Euro, AlertCircle } from 'lucide-react';
import { cn } from '../../shared/visualTokens';
import { t } from '../../i18n';

const KpiCard = ({ icon: Icon, label, value, alertTone = false, loading, testId }) => (
  <div
    className={cn(
      'rounded-2xl border bg-white p-4 shadow-[0_4px_18px_rgba(15,23,42,0.04)]',
      alertTone ? 'border-rose-200' : 'border-slate-200'
    )}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-2">
      <div
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-lg',
          alertTone ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-500'
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </span>
    </div>
    {loading ? (
      <div className="h-7 w-16 rounded bg-slate-100 animate-pulse" />
    ) : (
      <div className={cn('text-2xl font-bold', alertTone ? 'text-rose-700' : 'text-slate-900')}>
        {value}
      </div>
    )}
  </div>
);

const OperationsLiveKpiStrip = ({ kpis, loading, locale = 'fr' }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="adminv3-operations-live-kpi-strip">
      <KpiCard
        icon={Activity}
        label={t('operations.live.kpi.activeBookings', locale)}
        value={kpis.activeBookings}
        loading={loading}
        testId="adminv3-operations-live-kpi-active-bookings"
      />
      <KpiCard
        icon={AlertTriangle}
        label={t('operations.live.kpi.anomalies', locale)}
        value={kpis.anomalyCount}
        alertTone={kpis.anomalyCount > 0}
        loading={loading}
        testId="adminv3-operations-live-kpi-anomalies"
      />
      <KpiCard
        icon={Car}
        label={t('operations.live.kpi.onlineDrivers', locale)}
        value={kpis.onlineDrivers}
        loading={loading}
        testId="adminv3-operations-live-kpi-online-drivers"
      />
      <KpiCard
        icon={Euro}
        label={t('operations.live.kpi.pendingPayouts', locale)}
        value={kpis.pendingPayouts}
        loading={loading}
        testId="adminv3-operations-live-kpi-pending-payouts"
      />
      <KpiCard
        icon={AlertCircle}
        label={t('operations.live.kpi.openIncidents', locale)}
        value={kpis.openIncidents}
        alertTone={kpis.openIncidents > 0}
        loading={loading}
        testId="adminv3-operations-live-kpi-open-incidents"
      />
    </div>
  );
};

export default OperationsLiveKpiStrip;
